import { useCallback, useEffect, useRef, useState } from 'react';
import { foldPlaceName, foldStreetText } from '../records/mailingAddress';
import { comparablePostalCode, normaliseAddress, normaliseText } from '../records/normalise';
import { US_STATE_NAMES } from '../records/taxExemption';
import { t } from '../strings';
import type { AddressConfidenceNote } from './addressConfidence';

/**
 * Address Verification (piece 4; decisions 0031, 0089): four stored states over
 * the AddressProvider seam's four answers. Local format rules run first and
 * cost nothing; the metered call fires once the field settles, never per
 * keystroke; a provider match sets Verified without rewriting the typed text;
 * Corrected is a question put to a person and never stored; failure holds a
 * one-hour per-record retry; verification never blocks the parent save.
 */
/** The address's verification; NotApplicable is a Location with no address lines (decision 0673), and Estimated a street and postal code confirmed with the house number estimated (decision 7460). */
export type VerificationStatus = 'Unverified' | 'Verified' | 'Estimated' | 'NeedsReview' | 'Unverifiable' | 'NotApplicable';

export type SeamAnswer = 'Verified' | 'Corrected' | 'Estimated' | 'Unverifiable' | 'NotAttempted';

export interface Address {
  countryCode: string;
  addressLines: string[];
  dependentLocality?: string | null;
  /** The mailing city — the only city a document prints (decision 0406). */
  locality?: string | null;
  /** The town, township or borough the building stands in (decision 0406); informational, never compared as a correction. */
  municipality?: string | null;
  administrativeArea?: string | null;
  /** The county, parish or borough the provider answers where it answers one (decision 0354); never compared as a correction. */
  county?: string | null;
  postalCode?: string | null;
}

/**
 * One standardised address a provider offers, with the parts that differ from
 * what was typed (decision 0406). One candidate is the Accept-or-keep question
 * decision 0338 built; several is a pick list; none is a refusal.
 */
export interface AddressCandidate {
  address: Address;
  corrections: readonly AddressCorrection[];
  /** The provider keys that offered this address, where the binding asks more than one service (decision 0983). */
  sources?: readonly string[];
  /** 🔒 What the service could not confirm: the unit, where the Postal Service confirmed the building alone (decision 3120). */
  openQuestion?: 'unit';
}

/** The services a candidate can name as its source, each with the words the Location page reads (decisions 0983, 3120). */
export const ADDRESS_SOURCE_LABEL_KEYS = {
  usps: 'address.source.usps',
  google: 'address.source.google',
  openstreetmap: 'address.source.openstreetmap',
  census: 'address.source.census',
  geoapify: 'address.source.geoapify',
} as const;

/** Each service's name where several are named together: *USPS and the Census Bureau agree* (decision 3120). */
export const ADDRESS_SOURCE_NAME_KEYS = {
  usps: 'address.sourceName.usps',
  google: 'address.sourceName.google',
  openstreetmap: 'address.sourceName.openstreetmap',
  census: 'address.sourceName.census',
  geoapify: 'address.sourceName.geoapify',
} as const;

/**
 * What a provider confirmed when it could not confirm the building, so an
 * Unverifiable answer says what to check rather than nothing (decision 0406).
 */
export type VerificationEvidence = 'none' | 'street' | 'postalArea';

export interface AddressCorrection {
  field: keyof Address;
  from: string | null;
  to: string | null;
}

export interface SeamResult {
  status: SeamAnswer;
  normalized?: Address;
  corrections: readonly AddressCorrection[];
  /** Every standardised address on offer; the first is `normalized` (decision 0406). */
  candidates?: readonly AddressCandidate[];
  evidence?: VerificationEvidence;
}

export type VerifyFn = (address: Address, signal: AbortSignal) => Promise<SeamResult>;

/** The AddressFormatRule row for the field's country, declared by piece 2. */
export interface AddressFormatRuleRow {
  countryCode: string;
  postalCodePattern?: string | null;
  postalCodeExample: string;
  requiredFields: readonly string[];
  administrativeAreaLabel: string;
  postalCodeLabel: string;
  administrativeAreaRequired: boolean;
}

export const RETRY_AFTER_MS = 60 * 60 * 1000;
export const SETTLE_MS = 600;

/** The local check: a required field missing or a postal code off the row's pattern fails instantly, before any metered call. */
export function localFormatFailure(address: Address, rule: AddressFormatRuleRow): { field: keyof Address; message: 'required' | 'pattern'; example?: string } | null {
  for (const field of rule.requiredFields) {
    const value = (address as unknown as Record<string, unknown>)[field];
    const present = Array.isArray(value) ? value.some((v) => String(v).trim() !== '') : typeof value === 'string' && value.trim() !== '';
    if (!present) return { field: field as keyof Address, message: 'required' };
  }
  if (rule.administrativeAreaRequired && !(address.administrativeArea ?? '').trim()) return { field: 'administrativeArea', message: 'required' };
  if (rule.postalCodePattern && address.postalCode && !new RegExp(rule.postalCodePattern).test(address.postalCode)) {
    return { field: 'postalCode', message: 'pattern', example: rule.postalCodeExample };
  }
  return null;
}

/** Decision 0089: the answer and the person's choice decide what is stored; Corrected never is. */
export function mapSeamAnswer(status: SeamAnswer, choice?: 'accept' | 'keep'): VerificationStatus {
  switch (status) {
    case 'Verified':
      return 'Verified';
    case 'Estimated':
      return 'Estimated';
    case 'Corrected':
      return choice === 'accept' ? 'Verified' : 'NeedsReview';
    case 'Unverifiable':
    case 'NotAttempted':
      return 'Unverifiable';
  }
}

export function canRetry(lastFailureAt: number | null, now: number, retryAfterMs = RETRY_AFTER_MS): boolean {
  return lastFailureAt === null || now - lastFailureAt >= retryAfterMs;
}

export interface VerificationState {
  status: VerificationStatus;
  /** A suggestion awaiting accept or keep-as-typed; the typed text is untouched while it waits. */
  suggestion: { normalized: Address; corrections: readonly AddressCorrection[] } | null;
  localFailure: ReturnType<typeof localFormatFailure>;
  providerCalls: number;
  lastFailureAt: number | null;
  pending: boolean;
}

export const initialVerification: VerificationState = { status: 'Unverified', suggestion: null, localFailure: null, providerCalls: 0, lastFailureAt: null, pending: false };

export interface AddressVerificationOptions {
  verify: VerifyFn;
  rule: AddressFormatRuleRow;
  /** True for a Location already stored as Verified: selecting it fires no call. */
  alreadyVerified?: boolean;
  settleMs?: number;
  retryAfterMs?: number;
  now?: () => number;
  /** The stored state to open on: a record page hands in what the record holds. */
  initial?: Partial<VerificationState>;
}

export function useAddressVerification({ verify, rule, alreadyVerified = false, settleMs = SETTLE_MS, retryAfterMs = RETRY_AFTER_MS, now = Date.now, initial }: AddressVerificationOptions) {
  const [state, setState] = useState<VerificationState>(() => ({ ...initialVerification, ...(alreadyVerified ? { status: 'Verified' as const } : {}), ...initial }));
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    inFlight.current?.abort();
    inFlight.current = null;
  }, []);
  useEffect(() => cancel, [cancel]);

  const run = useCallback(
    async (address: Address) => {
      const controller = new AbortController();
      inFlight.current = controller;
      setState((s) => ({ ...s, pending: true, providerCalls: s.providerCalls + 1 }));
      try {
        const result = await verify(address, controller.signal);
        if (controller.signal.aborted) return;
        if (result.status === 'Corrected' && result.normalized) {
          setState((s) => ({ ...s, pending: false, suggestion: { normalized: result.normalized!, corrections: result.corrections }, lastFailureAt: null }));
        } else {
          setState((s) => ({ ...s, pending: false, suggestion: null, status: mapSeamAnswer(result.status), lastFailureAt: null }));
        }
      } catch {
        if (controller.signal.aborted) return;
        setState((s) => ({ ...s, pending: false, lastFailureAt: now() }));
      }
    },
    [verify, now],
  );

  /** Called as the address changes: the local rule answers now, and the metered call waits for the field to settle. */
  const addressChanged = useCallback(
    (address: Address) => {
      cancel();
      const failure = localFormatFailure(address, rule);
      setState((s) => ({ ...s, localFailure: failure, suggestion: null, status: s.status === 'Verified' ? 'Unverified' : s.status }));
      if (failure) return;
      timer.current = setTimeout(() => void run(address), settleMs);
    },
    [cancel, rule, run, settleMs],
  );

  /** Re-verify Address: offered on a Verified record too, subject to the one-hour retry after a failure. */
  const reverify = useCallback(
    (address: Address) => {
      if (!canRetry(state.lastFailureAt, now(), retryAfterMs)) return false;
      cancel();
      const failure = localFormatFailure(address, rule);
      setState((s) => ({ ...s, localFailure: failure }));
      if (failure) return false;
      void run(address);
      return true;
    },
    [cancel, now, retryAfterMs, rule, run, state.lastFailureAt],
  );

  /** Accepting the suggestion rewrites the fields and stores Verified; keeping what was typed stores NeedsReview. */
  const suggestion = state.suggestion;
  const resolveSuggestion = useCallback(
    (choice: 'accept' | 'keep'): Address | null => {
      if (!suggestion) return null;
      setState((s) => ({ ...s, suggestion: null, status: mapSeamAnswer('Corrected', choice) }));
      return choice === 'accept' ? suggestion.normalized : null;
    },
    [suggestion],
  );

  return { state, addressChanged, reverify, resolveSuggestion, retryAvailable: canRetry(state.lastFailureAt, now(), retryAfterMs) };
}

/**
 * The verification cache's key (decision 0338): the typed address folded the
 * way the duplicate match folds it, with the country kept apart as the row's
 * second key. Two typings of one address share a row, so the second costs
 * no vendor call.
 */
export function verificationCacheKey(address: Address): string {
  return normaliseAddress([...address.addressLines, address.dependentLocality, address.locality, address.administrativeArea, address.postalCode]);
}

/**
 * How many days an Unverifiable answer is served from the cache before the
 * bound provider is asked again (piece 21). 💰 The cache had no staleness
 * window of any kind, so one refusal stood for ever: a real address that
 * answered Unverifiable before the rules that decide a city changed kept
 * answering Unverifiable afterwards, and pressing Verify address changed
 * nothing (found in review, 2026-09-15). The Organization-scoped
 * `address.unverifiableCacheDays` row overrides the number.
 */
export const ADDRESS_UNVERIFIABLE_CACHE_KEY = 'address.unverifiableCacheDays';
export const DEFAULT_ADDRESS_UNVERIFIABLE_CACHE_DAYS = 7;

/**
 * 🔒 How many bound address services must name one building for Verified
 * (piece 21; decision 3121): an Organization setting on Settings ›
 * Integrations › Maps, a whole number from 1 to 3, read as 2 while no row holds
 * one. Three naming one building verify whatever a fourth says, so a count above
 * three would change nothing.
 */
export const ADDRESS_AGREEMENT_COUNT_KEY = 'address.agreementCount';
export const DEFAULT_ADDRESS_AGREEMENT_COUNT = 2;
export const MAX_ADDRESS_AGREEMENT_COUNT = 3;

/** The count a held setting names, or the default where the row is absent or not a whole number from 1 to 3. */
export function addressAgreementCount(held: unknown): number {
  const count = typeof held === 'number' ? held : typeof held === 'string' && held.trim() !== '' ? Number(held) : Number.NaN;
  return Number.isInteger(count) && count >= 1 && count <= MAX_ADDRESS_AGREEMENT_COUNT ? count : DEFAULT_ADDRESS_AGREEMENT_COUNT;
}

/** The refusal a typed agreement count earns, or null where it is a whole number from 1 to 3. */
export function addressAgreementCountProblem(value: unknown): string | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= MAX_ADDRESS_AGREEMENT_COUNT ? null : 'the number of address services that must agree is a whole number from 1 to 3';
}

/**
 * 🔒 What the bound services' agreement came to (decision 3121), carried on the
 * answer and its cache row and named on the Location page.
 */
export interface AddressAgreement {
  /** The adapters asked, in rank order: every bound one that could answer this address. */
  asked: readonly string[];
  /** The adapters that named at least one building. */
  answered: readonly string[];
  /** The adapters that named the leading building, in rank order. */
  agreeing: readonly string[];
  /** How many had to agree: the setting, never more than the adapters asked. */
  needed: number;
  /** True where the agreement reached the count, or three agreed whatever a fourth said. */
  met: boolean;
  /** True where the answers named more than one building. */
  disagree: boolean;
}

/** The number a held setting names, or the default where the row is absent or not a usable number of days. */
export function addressUnverifiableCacheDays(held: unknown): number {
  const days = typeof held === 'number' ? held : typeof held === 'string' ? Number(held) : Number.NaN;
  return Number.isFinite(days) && days >= 0 ? days : DEFAULT_ADDRESS_UNVERIFIABLE_CACHE_DAYS;
}

const COMPARED_FIELDS: readonly (keyof Address)[] = ['addressLines', 'dependentLocality', 'locality', 'administrativeArea', 'postalCode', 'countryCode'];

/** One field as text: the lines joined, a blank as null. */
export function addressFieldText(address: Address, field: keyof Address): string | null {
  const value = address[field];
  const text = Array.isArray(value) ? value.map((line) => line.trim()).filter(Boolean).join(', ') : typeof value === 'string' ? value.trim() : '';
  return text === '' ? null : text;
}

/**
 * How one field is folded before it is compared. A street line folds its
 * abbreviations, so a provider answering Lane where a person typed Ln has
 * standardised nothing; a place name folds its civil-division word, so Hopewell
 * and Hopewell Township are one place (decision 0406). Everything else folds
 * case, accents and punctuation alone.
 */
function foldField(field: keyof Address, value: string | null): string {
  if (field === 'addressLines') return foldStreetText(value ?? '');
  if (field === 'locality' || field === 'dependentLocality') return foldPlaceName(value ?? '');
  return normaliseText(value ?? '');
}

/**
 * The field-by-field difference between what was typed and what the provider
 * standardised, folded so a provider that only recases or abbreviates a street
 * answers Verified rather than Corrected. Empty means the two are the same
 * address.
 */
export function addressCorrections(typed: Address, normalized: Address): AddressCorrection[] {
  const out: AddressCorrection[] = [];
  for (const field of COMPARED_FIELDS) {
    const from = addressFieldText(typed, field);
    const to = addressFieldText(normalized, field);
    if (foldField(field, from) === foldField(field, to)) continue;
    out.push({ field, from, to });
  }
  return out;
}

/**
 * 🔒 Whether a provider's one candidate is the typed address itself (decision
 * 1923, the owner's *"no changes outside capitalization or abbreviations"*).
 * The seam already folds case, punctuation, spacing and the street words
 * (St/Street, N/North, Ste/Suite), so a correction it still lists is a change,
 * except two: a postal code whose five digits agree, the plus-four added, and
 * a state named by its code on one side and its name on the other. A part the
 * provider filled where nothing was typed is a change.
 */
export function isCleanAddressMatch(corrections: readonly AddressCorrection[]): boolean {
  return corrections.every((correction) => {
    if (correction.field === 'postalCode') {
      const typed = comparablePostalCode(correction.from);
      return typed !== '' && typed === comparablePostalCode(correction.to);
    }
    if (correction.field === 'administrativeArea') {
      const typed = stateKey(correction.from);
      return typed !== '' && typed === stateKey(correction.to);
    }
    return false;
  });
}

/** A state as two spellings compare it: a United States name taken to its code, anything else folded as text. */
function stateKey(value: string | null): string {
  const text = normaliseText(value ?? '');
  if (!text) return '';
  for (const [code, name] of Object.entries(US_STATE_NAMES)) if (code.toLowerCase() === text || normaliseText(name) === text) return code;
  return text;
}

/** One standardised address turned into a candidate: the address, and what differs from what was typed. */
export function addressCandidate(typed: Address, standardised: Address): AddressCandidate {
  return { address: standardised, corrections: addressCorrections(typed, standardised) };
}

/** What the Location verify routes answer (decision 0338): the seam's outcome, the correction if one is offered, and what the record now holds. */
export type VerifyOutcome = 'Verified' | 'Corrected' | 'Estimated' | 'Unverifiable' | 'Failed';

export interface LocationVerifyAnswer {
  outcome: VerifyOutcome;
  /** The stored status after the call; null when no record was written, as on the form's step. */
  verificationStatus: VerificationStatus | null;
  normalized: Address | null;
  corrections: AddressCorrection[];
  /** Every address on offer, newest rule first (decision 0406); empty on Verified, Unverifiable and Failed. */
  candidates: AddressCandidate[];
  /** What the provider confirmed where it confirmed no building (decision 0406). */
  evidence: VerificationEvidence;
  /** The municipality the provider placed the building in, where it differs from the mailing city. */
  municipality: string | null;
  providerKey: string | null;
  fromCache: boolean;
  /** The provider's or the seam's own words on a failure. */
  message: string | null;
  /** 🔒 T3724: the service's own reason for refusing the address as typed, on an Unverifiable answer that carries one (piece 10). */
  reason?: string;
  /** 🔒 The instant the hour's hold ends, on a failed attempt and on the refusal while it stands; read in the viewer's clock (decision 2201). */
  heldUntil?: string;
  /** 🔒 The map service answered that it is busy (T1874; decision 2710): a Failed answer with no hold, drawn with Verify Again. */
  busy?: boolean;
  /** 🔒 How the bound services agreed, named on the verification line (decision 3121); absent on a failure and an older cache row. */
  agreement?: AddressAgreement | null;
  /** 🔒 Each service's low-confidence answer, kept beside the result and never counted toward it (decision 7430); absent on a failure and an older cache row. */
  notes?: AddressConfidenceNote[];
}

export type VerifyChoice = 'accept' | 'keep';

export interface VerifyStepState {
  answer: LocationVerifyAnswer | null;
  pending: boolean;
  /** The person's answer to a Corrected outcome; null while the question stands. */
  choice: VerifyChoice | null;
  /** Which candidate they picked, where several were offered (decision 0406). */
  candidateIndex: number | null;
}

export interface VerifyStepOptions {
  verify: (address: Address) => Promise<LocationVerifyAnswer>;
}

/**
 * The Verify step a Location page and its form share (decision 0338): one call
 * per press, the outcome held until the address changes, and a Corrected
 * answer standing as a question until Accept or Keep as typed answers it.
 * Accept hands back the standardised address for the caller to write into
 * its fields; nothing here touches a field itself.
 */
export function useVerifyStep({ verify }: VerifyStepOptions) {
  const [state, setState] = useState<VerifyStepState>({ answer: null, pending: false, choice: null, candidateIndex: null });
  const inFlight = useRef(0);

  const run = useCallback(
    async (address: Address) => {
      const call = ++inFlight.current;
      setState({ answer: null, pending: true, choice: null, candidateIndex: null });
      try {
        const answer = await verify(address);
        if (call === inFlight.current) setState({ answer, pending: false, choice: null, candidateIndex: null });
      } catch (error) {
        if (call === inFlight.current) setState({ answer: failedAnswer(error instanceof Error ? error.message : String(error)), pending: false, choice: null, candidateIndex: null });
      }
    },
    [verify],
  );

  /** Use this address: the candidate a person picked, or the only one on offer. */
  const accept = useCallback(
    (candidateIndex = 0): Address | null => {
      const answer = state.answer;
      if (answer?.outcome !== 'Corrected') return null;
      const picked = answer.candidates[candidateIndex]?.address ?? (candidateIndex === 0 ? answer.normalized : null);
      if (!picked) return null;
      setState((s) => ({ ...s, choice: 'accept', candidateIndex }));
      return picked;
    },
    [state.answer],
  );

  const keep = useCallback(() => {
    if (state.answer?.outcome !== 'Corrected') return;
    setState((s) => ({ ...s, choice: 'keep', candidateIndex: null }));
  }, [state.answer]);

  /** The address changed under the answer, so the answer no longer describes it. */
  const reset = useCallback(() => {
    inFlight.current += 1;
    setState({ answer: null, pending: false, choice: null, candidateIndex: null });
  }, []);

  const questionOpen = state.answer?.outcome === 'Corrected' && state.choice === null;
  return { state, run, accept, keep, reset, questionOpen };
}

/** The answer a caller that never reached the seam holds; every part empty but the words. */
export function failedAnswer(message: string, verificationStatus: VerificationStatus | null = null, heldUntil?: string): LocationVerifyAnswer {
  return { outcome: 'Failed', verificationStatus, normalized: null, corrections: [], candidates: [], evidence: 'none', municipality: null, providerKey: null, fromCache: false, message, ...(heldUntil ? { heldUntil } : {}) };
}

/**
 * 🔒 The map service was busy (T1874; decision 2710): Failed with no hour's hold, so the person can press Verify Again
 * in a moment, and the words say so rather than the provider's own.
 */
export function busyAnswer(verificationStatus: VerificationStatus | null = null): LocationVerifyAnswer {
  return { ...failedAnswer(t('address.busy'), verificationStatus), busy: true };
}
